import React from "react";
import { motion, useMotionValue, useTransform } from "framer-motion";
import { StatusPill } from "@/components/StatusPill";
import { AlertTriangle, ChevronRight, Trash2, Archive, ArchiveRestore } from "lucide-react";

const SWIPE_TRIGGER = 96;

function Row({ item, onOpen, onArchive, onDelete }) {
  const dragged = React.useRef(false);
  const x = useMotionValue(0);
  const archiveOpacity = useTransform(x, [-SWIPE_TRIGGER, -24, 0], [1, 0.45, 0]);
  const deleteOpacity = useTransform(x, [0, 24, SWIPE_TRIGGER], [0, 0.45, 1]);
  const archived = !!item.archived;
  const handleOpen = () => { if (dragged.current) return; onOpen && onOpen(); };

  const pct = item.total ? Math.round((item.done / item.total) * 100) : 0;
  const issuePct = item.total ? Math.round(((item.issues || 0) / item.total) * 100) : 0;
  const greenPct = Math.min(100 - issuePct, pct);

  return (
    <div className="relative overflow-hidden rounded-xl">
      {/* Delete — drag RIGHT */}
      <motion.div style={{ opacity: deleteOpacity }} className="absolute inset-y-0 left-0 z-0 flex w-1/2 items-center justify-start bg-rose-500 pl-5 text-white" data-testid={`shared-swipe-delete-${item.token}`}>
        <div className="flex flex-col items-center gap-1"><Trash2 className="h-5 w-5" /><span className="text-[11px] font-semibold">Remove</span></div>
      </motion.div>
      {/* Archive / Unarchive — drag LEFT */}
      <motion.div style={{ opacity: archiveOpacity }} className="absolute inset-y-0 right-0 z-0 flex w-1/2 items-center justify-end bg-sky-500 pr-5 text-white" data-testid={`shared-swipe-archive-${item.token}`}>
        <div className="flex flex-col items-center gap-1">{archived ? <ArchiveRestore className="h-5 w-5" /> : <Archive className="h-5 w-5" />}<span className="text-[11px] font-semibold">{archived ? "Restore" : "Archive"}</span></div>
      </motion.div>

      <motion.div
        drag="x"
        dragDirectionLock
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={0.9}
        whileTap={{ scale: 0.98 }}
        style={{ x }}
        onDragStart={() => { dragged.current = true; }}
        onDragEnd={(e, info) => {
          if (info.offset.x <= -SWIPE_TRIGGER) onArchive && onArchive(!archived);
          else if (info.offset.x >= SWIPE_TRIGGER) onDelete && onDelete();
          setTimeout(() => { dragged.current = false; }, 60);
        }}
        role="button"
        tabIndex={0}
        data-testid={`shared-card-${item.token}`}
        onClick={handleOpen}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && handleOpen()}
        className="relative z-10 flex w-full touch-pan-y cursor-pointer items-center gap-3 rounded-xl border border-sky-500/20 border-l-4 border-l-sky-500 bg-card p-4 text-left"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate font-semibold">{item.job_name || item.report_title || "Shared list"}</span>
            <span className="shrink-0 rounded-full bg-sky-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-sky-600 dark:text-sky-300">{archived ? "Archived" : "Received"}</span>
          </div>
          {item.report_title && item.job_name && (
            <div className="mt-0.5 truncate text-xs text-muted-foreground">{item.report_title}</div>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <StatusPill status={item.status} />
            <span className="text-xs text-muted-foreground">{item.done}/{item.total} done</span>
            {(item.issues || 0) > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-rose-500/15 px-2 py-0.5 text-[11px] font-bold text-rose-500" data-testid={`shared-issue-${item.token}`}>
                <AlertTriangle className="h-3 w-3" /> {item.issues} flagged
              </span>
            )}
          </div>
          <div className="mt-2 flex items-center gap-2">
            <div className="flex h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
              <div className="h-full bg-emerald-500" style={{ width: `${greenPct}%` }} />
              <div className="h-full bg-rose-500" style={{ width: `${issuePct}%` }} />
            </div>
            <span className={`text-xs font-bold ${issuePct > 0 ? "text-rose-500" : "text-emerald-600"}`}>{pct}%</span>
          </div>
        </div>
        <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground/60" />
      </motion.div>
    </div>
  );
}

export const SharedCard = React.memo(Row);
